const Status = [
  "PAYMENT_PENDING", // Agency confirmed, awaiting payment
  "PAYMENT_PARTIAL", // Some amount paid, not enough to confirm the reservation
  "PAYMENT_RESERVATION", // Reservation fee paid, reservation is confirmed and ready to go
  "PAYMENT_COMPLETE", // Full payment received, reservation is confirmed and ready to go
  "OVERPAID", // Client paid more than required, is owed money by the agency
  "REFUND_PENDING", // Reservation is cancelled, awaiting refund
  "REFUND_PARTIAL", // Some amount refunded, not enough to cancel the reservation
  "REFUND_COMPLETE", // Full refund received, reservation is cancelled and refunded
  "OVERREFUNDED", // Agency refunded more than required, is owed money by the client
  "NO_REFUND", // Reservation is cancelled, no refund is due
] as const;

const PaymentStatus = ["PENDING", "CAPTURED", "REFUNDED", "FAILED", "VOIDED"] as const;

function calculateTotal(input: {
  numberOfAdults: number;
  numberOfChildren: number;
  adultPrice: number;
  childPrice: number;
  discount: {
    type: "PERCENTAGE" | "FIXED";
    value: number;
  } | null;
}): number {
  const subtotal = input.numberOfAdults * input.adultPrice + input.numberOfChildren * input.childPrice;
  if (!input.discount) {
    return subtotal;
  }

  if (input.discount.type === "FIXED") {
    return Math.max(0, subtotal - input.discount.value);
  }

  return Math.max(0, subtotal * (1 - input.discount.value));
}

function calculateStatus(input: {
  totalPrice: number;
  totalPaid: number;
  totalRefundable: number;
  totalRefunded: number;
  totalReservationFee: number;
  cancelledAt: Date | null;
}): Reservation["Status"] {
  if (input.cancelledAt) {
    if (input.totalRefunded > input.totalRefundable) {
      return "OVERREFUNDED";
    }
    if (input.totalRefundable === 0) {
      return "NO_REFUND";
    }
    if (input.totalRefunded === 0) {
      return "REFUND_PENDING";
    }
    if (input.totalRefunded < input.totalRefundable) {
      return "REFUND_PARTIAL";
    }
    return "REFUND_COMPLETE";
  }
  if (input.totalPaid > input.totalPrice) {
    return "OVERPAID";
  }
  if (input.totalPaid === 0) {
    return "PAYMENT_PENDING";
  }
  if (input.totalPaid >= input.totalReservationFee && input.totalPaid < input.totalPrice) {
    return "PAYMENT_RESERVATION";
  }
  if (input.totalPaid < input.totalPrice) {
    return "PAYMENT_PARTIAL";
  }
  return "PAYMENT_COMPLETE";
}

function calculateFinancials(input: {
  totalPaid: number;
  totalRefunded: number;
  numberOfAdults: number;
  numberOfChildren: number;
  adultPrice: number;
  childPrice: number;
  adultReservationFee: number;
  childReservationFee: number;
  adultCancellationFee: number;
  childCancellationFee: number;
  discount: { type: "PERCENTAGE" | "FIXED"; value: number } | null;
  cancelledAt: Date | null;
  cancellationDeadline: Date | null;
  groupPricing: {
    minimumNumberOfPeople: number;
    adultPrice: number;
    childPrice: number;
  }[];
}) {
  const groupPricing = input.groupPricing
    .filter((group) => group.minimumNumberOfPeople <= input.numberOfAdults + input.numberOfChildren)
    .sort((a, b) => b.minimumNumberOfPeople - a.minimumNumberOfPeople)[0];

  const adultPrice = groupPricing?.adultPrice ?? input.adultPrice;
  const childPrice = groupPricing?.childPrice ?? input.childPrice;

  const totalPrice = calculateTotal({
    numberOfAdults: input.numberOfAdults,
    numberOfChildren: input.numberOfChildren,
    adultPrice,
    childPrice,
    discount: input.discount,
  });
  const calcTotalReservationFee = calculateTotal({
    numberOfAdults: input.numberOfAdults,
    numberOfChildren: input.numberOfChildren,
    adultPrice: input.adultReservationFee,
    childPrice: input.childReservationFee,
    discount: null,
  });
  const calcTotalCancellationFee = calculateTotal({
    numberOfAdults: input.numberOfAdults,
    numberOfChildren: input.numberOfChildren,
    adultPrice: input.adultCancellationFee,
    childPrice: input.childCancellationFee,
    discount: null,
  });

  let totalRefundable: number;
  let totalCancellationFee: number;

  if (input.cancelledAt) {
    if (input.cancellationDeadline === null || input.cancelledAt > input.cancellationDeadline) {
      totalRefundable = 0;
      totalCancellationFee = input.totalPaid;
    } else {
      const boundedCancellationFee = Math.min(calcTotalCancellationFee, input.totalPaid);
      totalRefundable = input.totalPaid - boundedCancellationFee;
      totalCancellationFee = boundedCancellationFee;
    }
  } else {
    if (input.cancellationDeadline && input.cancellationDeadline > new Date()) {
      totalRefundable = 0;
      totalCancellationFee = calcTotalCancellationFee;
    } else {
      totalRefundable = 0;
      totalCancellationFee = totalPrice;
    }
  }

  const amountDue = input.cancelledAt ? 0 : Math.max(0, totalPrice - input.totalPaid);
  const refundDue = input.cancelledAt ? Math.max(0, totalRefundable - input.totalRefunded) : 0;

  totalCancellationFee = Math.min(totalCancellationFee, totalPrice);
  const totalReservationFee = Math.min(calcTotalReservationFee, totalPrice);

  const status = calculateStatus({
    totalPrice,
    totalRefundable,
    totalPaid: input.totalPaid,
    totalRefunded: input.totalRefunded,
    cancelledAt: input.cancelledAt,
    totalReservationFee,
  });

  return {
    status,
    totalPrice,
    totalPaid: input.totalPaid,
    amountDue,
    totalReservationFee,
    totalCancellationFee,
    totalRefundable,
    totalRefunded: input.totalRefunded,
    refundDue,

    adultReservationFee: input.adultReservationFee,
    childReservationFee: input.childReservationFee,
    adultCancellationFee: input.adultCancellationFee,
    childCancellationFee: input.childCancellationFee,

    calculatedAdultPrice: adultPrice,
    calculatedChildPrice: childPrice,
  };
}

function calculatePaymentTotals(input: {
  success: boolean;
  pending: boolean;
  is_auth: boolean;
  is_captured: boolean;
  is_voided: boolean;
  is_refunded: boolean;
  amount_cents: number;
  refunded_amount_cents: number;
  captured_amount: number;
}): {
  capturedAmount: number;
  refundedAmount: number;
  paymentAmount: number;
  actualAmount: number;
  paymentStatus: (typeof PaymentStatus)[number];
} {
  // Determine payment status based on transaction state
  // Priority: voided > refunded > failed > pending > captured > authorized
  let paymentStatus: (typeof PaymentStatus)[number];
  if (input.is_voided) {
    paymentStatus = "VOIDED";
  } else if (input.is_refunded) {
    paymentStatus = "REFUNDED";
  } else if (!input.success && !input.pending) {
    paymentStatus = "FAILED";
  } else if (input.pending) {
    paymentStatus = "PENDING";
  } else if (input.is_captured) {
    paymentStatus = "CAPTURED";
  } else if (input.success) {
    paymentStatus = "CAPTURED";
  } else {
    paymentStatus = "PENDING";
  }

  // Payment amount is the display amount for the transaction
  const paymentAmount = input.is_captured ? input.captured_amount : input.amount_cents;

  // Actual amount to add to trip revenue and customer totalPaid
  // - Use captured amount if payment is captured
  // - Use authorized amount if only authorized (not yet captured)
  // - Zero for failed, pending, voided, or refund transactions
  const actualAmount = !input.success ? 0 : input.is_captured ? input.captured_amount : input.amount_cents;

  // Calculate amounts - voided transactions have zero effective amounts
  let capturedAmount = 0;
  let refundedAmount = 0;

  if (input.is_voided) {
    // Voided transactions have no effective amounts
    return {
      capturedAmount: 0,
      refundedAmount: 0,
      paymentAmount,
      actualAmount: 0,
      paymentStatus,
    };
  }

  if (input.success) {
    // Captured amount
    if (input.is_captured) {
      capturedAmount = input.captured_amount;
    } else if (!input.is_auth && !input.is_refunded) {
      // Direct payment (non-auth flow) - the full amount is captured
      capturedAmount = input.amount_cents;
    }

    // Refunded amount
    if (input.is_refunded) {
      refundedAmount = input.refunded_amount_cents;
    }
  }

  return {
    capturedAmount,
    refundedAmount,
    paymentAmount,
    actualAmount,
    paymentStatus,
  };
}

export const Reservation = {
  Status: Status,
  PaymentStatus: PaymentStatus,
  CalculateTotal: calculateTotal,
  CalculatePaymentTotals: calculatePaymentTotals,
  CalculateFinancials: calculateFinancials,
};
export type Reservation = {
  Status: (typeof Status)[number];
  PaymentStatus: (typeof PaymentStatus)[number];
  CalculateTotal: typeof calculateTotal;
  CalculatePaymentTotals: typeof calculatePaymentTotals;
  CalculateFinancials: typeof calculateFinancials;
};
